import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {CloudIcon, CloudOfflineIcon} from '@primer/octicons-react'
import {Label, RelativeTime} from '@primer/react'
import type {Deployment} from '../types'
import styles from './DeploymentRecordListItem.module.css'

interface DeploymentRecordListItemProps {
  deployment: Deployment
}

export function DeploymentRecordListItem({deployment}: DeploymentRecordListItemProps) {
  const interpunct = '·'
  const detectionDate = new Date(Date.parse(deployment.created_at)).toLocaleDateString('default', {
    month: 'short',
    day: 'numeric',
  })
  const hasLastUpdated = deployment.updated_at && deployment.updated_at !== deployment.created_at

  const leadingIcon = deployment.deleted_at ? (
    <CloudOfflineIcon size={16} className="fgColor-attention" />
  ) : (
    <CloudIcon size={16} className="fgColor-success" />
  )

  // first letter uppercase for display purposes
  const display_logical_env =
    deployment.logical_environment.charAt(0).toUpperCase() + deployment.logical_environment.slice(1).toLowerCase()

  // Build accessible title that includes deployment all relevant information
  const accessibleTitle = `${deployment.deployment_name} deployed to ${display_logical_env} environment`
  return (
    <ListItem
      title={
        <ListItemTitle
          value={deployment.deployment_name}
          aria-label={accessibleTitle}
          containerClassName={styles.ListItemContainer}
        >
          <Label variant="secondary" size="small" className={styles.ListItemTitleLabel}>
            {display_logical_env}
          </Label>
        </ListItemTitle>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>{leadingIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {deployment.runtime_risks.length > 0 && (
          <div className={styles.TagsContainer}>
            {deployment.runtime_risks.map(risk => (
              <Label key={`${deployment.deployment_name}-${risk}`} className={styles.TagLabel} size="small">
                {risk}
              </Label>
            ))}
          </div>
        )}
        <ListItemDescription className="gap-1">
          {deployment.physical_environment && (
            <ListItemDescriptionItem>{`${deployment.physical_environment} ${interpunct}`}</ListItemDescriptionItem>
          )}

          {deployment.cluster && (
            <ListItemDescriptionItem>{`${deployment.cluster} ${interpunct}`}</ListItemDescriptionItem>
          )}

          <ListItemDescriptionItem>Detected on {detectionDate}</ListItemDescriptionItem>

          {hasLastUpdated && (
            <ListItemDescriptionItem>
              {`${interpunct} Updated`} <RelativeTime datetime={deployment.updated_at} />
            </ListItemDescriptionItem>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

try{ DeploymentRecordListItem.displayName ||= 'DeploymentRecordListItem' } catch {}